import { useCallback, useRef } from 'react';

import type {
	MissingValue,
	PreloadQueryVarSafeMetadata,
	PreloadQueryVarSideValue,
	PreloadQueryVarValueType,
	PreloadQueryVarsDiffReport,
	PreloadQueryVarsMismatchField,
	UndefinedValue,
} from '../../../common/types/preload-query-vars-diff';
import { type SearchPreloadDataProvider } from '../../../common/types/search-preload';
import { onPreloadQueryVarsDiff } from '../../../common/utils/events/preload-query-vars-diff-event';
import { useCommonAndPrivateAttributes, useAnalytics } from '../../../controllers/analytics';
import { getPreloadedQueryVars } from './get-preloaded-query-vars';
// eslint-disable-next-line no-barrel-files/no-barrel-files
export { getPreloadedQueryVars } from './get-preloaded-query-vars';

const ROOT_PATH = '';
const MISSING_VALUE: MissingValue = '__missing__';
const UNDEFINED_VALUE: UndefinedValue = '__undefined__';
const ALLOWED_DIFF_KEYS = new Set(['sessionId', 'expectResult']);
const ALLOWED_VALUE_FIELD_PREFIXES = ['experimentLayers', 'fanoutExperimentCohort'];

function isRecord(value: unknown): value is Record<string, unknown> {
	return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function getObjectKeys(value: Record<string, unknown>) {
	return Object.keys(value).sort();
}

function buildPath(basePath: string, key: string) {
	return basePath ? `${basePath}.${key}` : key;
}

function buildArrayPath(basePath: string, index: number) {
	return `${basePath}[${index}]`;
}

function hasOwnKey(value: Record<string, unknown>, key: string) {
	return Object.prototype.hasOwnProperty.call(value, key);
}

function isMissingOrUndefinedMismatchEqual(
	left: Record<string, unknown>,
	right: Record<string, unknown>,
	key: string,
) {
	const leftHasKey = hasOwnKey(left, key);
	const rightHasKey = hasOwnKey(right, key);

	if (leftHasKey === rightHasKey) {
		return false;
	}

	return leftHasKey ? left[key] === undefined : right[key] === undefined;
}

function shouldIncludeValues(fieldName: string) {
	return ALLOWED_VALUE_FIELD_PREFIXES.some((prefix) => fieldName.startsWith(prefix));
}

function getSideValue(value: unknown, hasValue: boolean): PreloadQueryVarSideValue {
	if (!hasValue) {
		return MISSING_VALUE;
	}

	return value === undefined ? UNDEFINED_VALUE : value;
}

function getValueType(value: unknown): PreloadQueryVarValueType {
	if (value === null) {
		return 'null';
	}

	if (Array.isArray(value)) {
		return 'array';
	}

	if (value === undefined) {
		return 'undefined';
	}

	if (typeof value === 'boolean') {
		return 'boolean';
	}

	if (typeof value === 'number') {
		return 'number';
	}

	if (typeof value === 'string') {
		return 'string';
	}

	return 'object';
}

function getSideMetadata(value: unknown, hasValue: boolean): PreloadQueryVarSafeMetadata {
	if (!hasValue) {
		return { state: 'missing' };
	}

	if (value === undefined) {
		return { state: 'undefined', type: 'undefined' };
	}

	if (value === null) {
		return { state: 'null', type: 'null' };
	}

	if (Array.isArray(value)) {
		return {
			state: 'present',
			type: 'array',
			arrayLength: value.length,
		};
	}

	if (isRecord(value)) {
		return {
			state: 'present',
			type: 'object',
			objectKeyCount: Object.keys(value).length,
		};
	}

	return {
		state: 'present',
		type: getValueType(value),
	};
}

function createMismatchField(
	fieldName: string,
	ssr: unknown,
	spa: unknown,
	ssrHasValue: boolean = true,
	spaHasValue: boolean = true,
): PreloadQueryVarsMismatchField {
	if (!shouldIncludeValues(fieldName)) {
		return {
			fieldName,
			ssr: getSideMetadata(ssr, ssrHasValue),
			spa: getSideMetadata(spa, spaHasValue),
		};
	}

	return {
		fieldName,
		ssr: getSideValue(ssr, ssrHasValue),
		spa: getSideValue(spa, spaHasValue),
	};
}

export function getSearchQueryVariableMismatchFields(
	left: unknown,
	right: unknown,
	path: string = ROOT_PATH,
): PreloadQueryVarsMismatchField[] {
	if (Object.is(left, right)) {
		return [];
	}

	if (Array.isArray(left) || Array.isArray(right)) {
		if (!Array.isArray(left) || !Array.isArray(right)) {
			return [createMismatchField(path, left, right)];
		}

		const mismatchFields: PreloadQueryVarsMismatchField[] = [];
		const maxLength = Math.max(left.length, right.length);

		if (left.length !== right.length) {
			mismatchFields.push(createMismatchField(`${path}.length`, left.length, right.length));
		}

		for (let index = 0; index < maxLength; index++) {
			const arrayPath = buildArrayPath(path, index);
			const leftHasIndex = index in left;
			const rightHasIndex = index in right;

			if (!leftHasIndex || !rightHasIndex) {
				mismatchFields.push(
					createMismatchField(arrayPath, left[index], right[index], leftHasIndex, rightHasIndex),
				);
				continue;
			}

			mismatchFields.push(
				...getSearchQueryVariableMismatchFields(left[index], right[index], arrayPath),
			);
		}

		return mismatchFields;
	}

	if (isRecord(left) || isRecord(right)) {
		if (!isRecord(left) || !isRecord(right)) {
			return [createMismatchField(path, left, right)];
		}

		const mismatchFields: PreloadQueryVarsMismatchField[] = [];
		const allKeys = getObjectKeys({ ...left, ...right }).filter(
			(key) => !ALLOWED_DIFF_KEYS.has(key),
		);

		for (const key of allKeys) {
			const fieldPath = buildPath(path, key);
			const leftHasKey = hasOwnKey(left, key);
			const rightHasKey = hasOwnKey(right, key);

			if (!leftHasKey || !rightHasKey) {
				if (!isMissingOrUndefinedMismatchEqual(left, right, key)) {
					mismatchFields.push(
						createMismatchField(fieldPath, left[key], right[key], leftHasKey, rightHasKey),
					);
				}
				continue;
			}

			mismatchFields.push(
				...getSearchQueryVariableMismatchFields(left[key], right[key], fieldPath),
			);
		}

		return mismatchFields;
	}

	return [createMismatchField(path, left, right)];
}

export function getPreloadQueryVarsDiff(
	ssrSearchQueryVariables: unknown,
	spaSearchQueryVariables: unknown,
): {
	match: boolean;
	mismatchCount: number;
	mismatchFields: PreloadQueryVarsMismatchField[];
} {
	const mismatchFields = getSearchQueryVariableMismatchFields(
		ssrSearchQueryVariables,
		spaSearchQueryVariables,
	);

	return {
		match: mismatchFields.length === 0,
		mismatchCount: mismatchFields.length,
		mismatchFields,
	};
}

export function getPreloadQueryVarsDiffReport(
	source: string | undefined,
	ssrSearchResultsQueryVariables: unknown,
	spaSearchResultsQueryVariables: unknown,
	ssrRightPanelQueryVariables: unknown,
	spaRightPanelQueryVariables: unknown,
): PreloadQueryVarsDiffReport {
	const search = getPreloadQueryVarsDiff(
		ssrSearchResultsQueryVariables,
		spaSearchResultsQueryVariables,
	);
	const rightPanel = getPreloadQueryVarsDiff(
		ssrRightPanelQueryVariables,
		spaRightPanelQueryVariables,
	);
	const mismatchCount = search.mismatchCount + rightPanel.mismatchCount;

	return {
		source: source ?? '',
		match: mismatchCount === 0,
		mismatchCount,
		search,
		rightPanel,
		experimentLayers: {
			ssr: {
				searchResultsQueryVariables: isRecord(ssrSearchResultsQueryVariables)
					? ssrSearchResultsQueryVariables.experimentLayers
					: undefined,
				rightPanelQueryVariables: isRecord(ssrRightPanelQueryVariables)
					? ssrRightPanelQueryVariables.experimentLayers
					: undefined,
			},
			spa: {
				searchResultsQueryVariables: isRecord(spaSearchResultsQueryVariables)
					? spaSearchResultsQueryVariables.experimentLayers
					: undefined,
				rightPanelQueryVariables: isRecord(spaRightPanelQueryVariables)
					? spaRightPanelQueryVariables.experimentLayers
					: undefined,
			},
		},
	};
}

export type ReportPreloadQueryVarsDiff = ({
	cloudId,
	searchResultsQueryVariables,
	rightPanelQueryVariables,
}: {
	cloudId?: string;
	searchResultsQueryVariables: unknown;
	rightPanelQueryVariables: unknown;
}) => void;

export function usePreloadQueryVarsDiffReporter(
	preloadDataProvider?: SearchPreloadDataProvider,
): ReportPreloadQueryVarsDiff {
	const { fireAnalyticsEvent } = useAnalytics();
	const { commonAttributes } = useCommonAndPrivateAttributes();
	const hasReportedRef = useRef(false);

	const firePreloadQueryVarsDiffAnalytics = useCallback(
		(diff: PreloadQueryVarsDiffReport) => {
			const event = onPreloadQueryVarsDiff(diff);
			fireAnalyticsEvent({
				...event,
				attributes: {
					commonAttributes,
					additionalAttributes: event.attributes.additionalAttributes,
				},
			});
		},
		[commonAttributes, fireAnalyticsEvent],
	);

	return useCallback<ReportPreloadQueryVarsDiff>(
		({ cloudId, searchResultsQueryVariables, rightPanelQueryVariables }) => {
			if (hasReportedRef.current) {
				return;
			}

			const preloadedQueryVars = getPreloadedQueryVars({
				cloudId,
				preloadDataProvider,
			});
			if (!preloadedQueryVars) {
				return;
			}

			hasReportedRef.current = true;

			const diff = getPreloadQueryVarsDiffReport(
				preloadedQueryVars.source,
				preloadedQueryVars.searchResultsQueryVariables,
				searchResultsQueryVariables,
				preloadedQueryVars.rightPanelQueryVariables,
				rightPanelQueryVariables,
			);
			firePreloadQueryVarsDiffAnalytics(diff);
		},
		[firePreloadQueryVarsDiffAnalytics, preloadDataProvider],
	);
}
